import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CButton, CCard, CCardBody, CCardHeader, CCol, CForm, CRow } from "@coreui/react";
import { CSpinnerText } from "@tradingdroid/frontends-components";
import {
  useBacktests,
  useExchanges,
  useExecutions,
  useJobPolling,
  useStrategies,
} from "@src/hooks/redux";
import { useCommonSettingsUrlParams } from "@src/hooks/useCommonSettingsUrlParams";
import { useQuoteCurrency } from "@src/hooks/useQuoteCurrency";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import { COMMAND_POLL_MAX_ATTEMPTS, EXECUTION_POLL_DELAY_MS } from "@src/utils/constants";
import { delay, formatToISO8601WithTimezone } from "@src/utils/dateUtils";
import { formatBacktestDataFromSource } from "@src/utils/formatters";
import dayjs from "dayjs";
import isPlainObject from "lodash-es/isPlainObject";
import pick from "lodash-es/pick";

import type { ExchangeEnvironment } from "@src/types";
import type * as TDT from "@tradingdroid/frontends-types";
import { buildDefaultConfig } from "@src/utils/configBuilder";
import {
  BACKTEST_ONLY_ENTRY_POLICY_KEYS,
  LEGACY_COST_TOGGLE_KEYS,
  splitRunConfig,
  validateRunConfigShape,
} from "@src/utils/runConfigSections";

import { CJsonTextArea } from "../components/CJsonTextArea";
import { CommonSettingsBlock } from "../components/CommonSettingsBlock";
import { ExecutionConfigAuth } from "../components/ExecutionConfigAuth";

const getDefaultFormData = (type: "backtest" | "execution") => ({
  strategyId: 0,
  exchangeName: "",
  instrument: "",
  timeframe: "5m",
  startDateTime: dayjs().subtract(1, "year").startOf("day").format("YYYY-MM-DDTHH:mm"),
  endDateTime: dayjs().subtract(1, "day").endOf("day").format("YYYY-MM-DDTHH:mm"),
  investmentAmount: type === "backtest" ? 1000 : 20,
  cumulativeInvestment: true,
  config: "{}",
  exchangeEnvironment: "qa" as ExchangeEnvironment,
  infisicalKeyPath: "",
  infisicalSecretPath: "",
  infisicalPasswordPath: "",
});

/**
 * Build default config from strategy parameters_schema.
 * Extracts default values from strategy_config and exit_config sections.
 */
interface Props {
  type: "backtest" | "execution";
}

/**
 * Unified runner for creating both backtests and executions
 */
export const RunRunnerPage: React.FC<Props> = ({ type }) => {
  const navigate = useNavigate();
  const { showToast } = useToastActions();
  const [searchParams, setSearchParams] = useSearchParams();
  const { trackActiveJob, syncActiveJobs } = useJobPolling();
  const { getStrategyById } = useStrategies();

  const { backtestFromExternal, clearBacktestFromExternal } = useBacktests();
  const { upsertExecution } = useExecutions();
  const { getExchange } = useExchanges();

  const [running, setRunning] = useState(false);
  const [waitingMessage, setWaitingMessage] = useState("");
  const [origin, setOrigin] = useState<TDT.ApiJobOrigin | undefined>(undefined);
  const mountedRef = useRef(true);

  // Cleanup on unmount to prevent state updates on unmounted component
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [formData, setFormData] = useState(getDefaultFormData(type));
  const selectedStrategy = getStrategyById(formData.strategyId);
  const strategyName = selectedStrategy?.name ?? "Strategy";
  const selectedExchange = getExchange(formData.exchangeName);
  // When selectedExchange exists, its `type` is always defined (required in DB with default "cex").
  // The "cex" fallback only applies when no exchange is selected yet; most exchanges are CEX.
  const exchangeType = selectedExchange?.type ?? "cex";
  const quoteCurrency = useQuoteCurrency(formData.exchangeName, formData.instrument);

  const handleInputChange = (field: string, value: string | number | boolean) => {
    if (field === "strategyId") {
      const strategyId = typeof value === "number" ? value : Number(value);
      const normalizedStrategyId = Number.isFinite(strategyId) ? strategyId : 0;
      const strategy = normalizedStrategyId ? getStrategyById(normalizedStrategyId) : undefined;
      setFormData((prev) => ({
        ...prev,
        strategyId: normalizedStrategyId,
        config: strategy ? JSON.stringify(buildDefaultConfig(strategy), null, 2) : "{}",
      }));
      // The config is rebuilt from the new strategy's defaults, so a loaded origin no longer
      // describes this run. For a backtest that leaves the reversal policy out, the API decides
      // it from the origin's source, so a stale origin would apply that source's policy or
      // refuse the run.
      setOrigin(undefined);
      return;
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Load common settings from URL params (for dev reload persistence)
  const handleUrlParamsLoad = useCallback(
    (settings: Partial<ReturnType<typeof getDefaultFormData>>) => {
      setFormData((prev) => {
        const updated = { ...prev, ...settings };
        // Also load strategy default config if strategy_id was provided
        if (settings.strategyId) {
          const strategy = getStrategyById(settings.strategyId);
          if (strategy) {
            updated.config = JSON.stringify(buildDefaultConfig(strategy), null, 2);
          }
        }
        return updated;
      });
    },
    [getStrategyById],
  );
  useCommonSettingsUrlParams({
    skip: !!searchParams.get("loadFrom"), // Skip if loading from external source
    onLoad: handleUrlParamsLoad,
  });

  // Load form data from external source (optimization trial, duplicate backtest, backtest→execution)
  useEffect(() => {
    const loadFrom = searchParams.get("loadFrom");
    if (!loadFrom || !backtestFromExternal) return;

    const importedBacktest = formatBacktestDataFromSource(backtestFromExternal);
    setFormData({
      ...getDefaultFormData(type),
      ...importedBacktest,
    });

    // Capture origin metadata for traceability (or clear if not present)
    setOrigin(backtestFromExternal.origin);

    setSearchParams((p) => {
      p.delete("loadFrom");
      return p;
    });

    // Clear external data after consuming to prevent stale state
    clearBacktestFromExternal();

    const messages: Record<string, Record<string, string>> = {
      backtest: {
        optimization: "Loaded optimization trial for backtesting",
        duplicate: "Loaded backtest for duplication",
        fromJob: "Loaded backtest from job JSON",
      },
      execution: {
        backtest: "Loaded backtest into execution form",
        duplicate: "Loaded execution for duplication",
        fromJob: "Loaded execution from job JSON",
      },
    };
    const message = messages[type]?.[loadFrom] ?? "Loaded external data";
    showToast({ message, type: "success" });
  }, [
    type,
    backtestFromExternal,
    searchParams,
    setSearchParams,
    showToast,
    clearBacktestFromExternal,
  ]);

  const validateForm = (parsedConfig: Record<string, unknown>) => {
    if (!parsedConfig || typeof parsedConfig !== "object" || Array.isArray(parsedConfig)) {
      return "Config must be a JSON object";
    }
    if (!formData.strategyId || formData.strategyId === 0) {
      return "Strategy is required";
    }
    if (!formData.exchangeName) {
      return "Exchange is required";
    }
    if (!formData.instrument.trim()) {
      return "Instrument is required";
    }
    if (Object.keys(parsedConfig).length === 0) {
      return "Config cannot be empty";
    }

    return validateRunConfigShape(parsedConfig, type);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Parse JSON once at the beginning
    let parsedConfig: Record<string, unknown>;
    try {
      parsedConfig = JSON.parse(formData.config);
    } catch {
      showToast({ message: "Invalid JSON in configuration", type: "danger" });
      return;
    }

    // Validate form with parsed config
    const validationError = validateForm(parsedConfig);
    if (validationError) {
      showToast({ message: validationError, type: "danger" });
      return;
    }

    setRunning(true);

    try {
      // Extract config sections from parsed JSON
      // cost_config is passed through with its own values when present (preserves all cost settings
      // from duplicates). Legacy top-level apply_slippage/apply_fees are supported for backward
      // compat: they fill a toggle that a cost_config object does not name, or build the cost map
      // when the JSON has none.
      const sections = splitRunConfig(parsedConfig);
      const {
        strategy_config,
        exit_config,
        regime_config,
        cost_config,
        apply_slippage,
        apply_fees,
        // Portfolio fields that don't have form equivalents - pass through to portfolio
        base_step_size,
        base_max_amount,
        base_min_amount,
      } = sections;

      // Build job payload with proper types - no unsafe casts needed
      let jobPayload: TDT.ApiJobCreate;

      if (type === "backtest") {
        const config: TDT.ApiBacktestConfig = {
          portfolio: {
            initial_capital: formData.investmentAmount,
            cumulative_investment: formData.cumulativeInvestment,
            base_step_size,
            base_max_amount,
            base_min_amount,
          },
          cost_config: isPlainObject(cost_config)
            ? { ...pick(sections, LEGACY_COST_TOGGLE_KEYS), ...cost_config }
            : (cost_config ?? {
                apply_slippage: apply_slippage ?? true,
                apply_fees: apply_fees ?? true,
              }),
          strategy_config: strategy_config ?? {},
          exit_config: exit_config ?? {},
          ...(regime_config && { regime_config }),
          ...pick(sections, BACKTEST_ONLY_ENTRY_POLICY_KEYS),
        };

        jobPayload = {
          job_type_name: "backtest",
          config: config,
          end_ts: formatToISO8601WithTimezone(formData.endDateTime),
          exchange_name: formData.exchangeName,
          from_ts: formatToISO8601WithTimezone(formData.startDateTime),
          period: formData.timeframe,
          strategy_id: formData.strategyId,
          symbol_name: formData.instrument,
          // Include origin metadata for traceability
          metadata: origin ? { origin } : undefined,
        };
      } else {
        const config: TDT.ApiLiveConfig = {
          portfolio: {
            initial_capital: formData.investmentAmount,
            cumulative_investment: formData.cumulativeInvestment,
            base_step_size,
            base_max_amount,
            base_min_amount,
          },
          strategy_config: strategy_config ?? {},
          exit_config: exit_config ?? {},
          ...(regime_config && { regime_config }),
          sandbox: formData.exchangeEnvironment === "qa",
          secrets_provider_config: {
            provider_type: "infisical",
            environment: formData.exchangeEnvironment,
            key_path: formData.infisicalKeyPath,
            secret_path: formData.infisicalSecretPath,
            password_path: formData.infisicalPasswordPath || null,
          },
        };

        jobPayload = {
          job_type_name: "live",
          config: config,
          exchange_name: formData.exchangeName,
          period: formData.timeframe,
          strategy_id: formData.strategyId,
          symbol_name: formData.instrument,
          // Include origin metadata for traceability (e.g., execution from backtest)
          metadata: origin ? { origin } : undefined,
        };
      }

      // Create the job via API
      const job = await tdApi.createJob(jobPayload);
      console.log(`[Job Creation] Backend created job ID: ${job?.id}`);

      if (!job || !job.id) {
        throw new Error(`Failed to create ${type} job`);
      }

      const entityLabel = type === "backtest" ? "Backtest" : "Execution";

      if (type === "backtest") {
        // For backtests: track job and navigate immediately
        trackActiveJob({
          jobId: job.id,
          jobType: "backtest",
          entityType: type,
          metadata: {
            title: `${entityLabel}: ${strategyName}`,
            description: `${formData.exchangeName} ${formData.instrument} ${formData.timeframe}`,
          },
        });
        syncActiveJobs();
        showToast({
          message: `${entityLabel} job (id: ${job.id}) started! You'll be notified when complete.`,
          type: "info",
        });
        navigate("/backtests");
      } else {
        // For executions: track job first so users have visibility even if page refresh or timeout
        trackActiveJob({
          jobId: job.id,
          jobType: "live",
          entityType: type,
          metadata: {
            title: `${entityLabel}: ${strategyName}`,
            description: `${formData.exchangeName} ${formData.instrument} ${formData.timeframe}`,
          },
        });
        syncActiveJobs();

        // Wait until execution entity is created before navigating
        // Using COMMAND_POLL_MAX_ATTEMPTS (30s) since this is a user-initiated action
        // that may take longer than background polling (15s)
        setWaitingMessage("Waiting for execution to start...");

        for (let attempt = 1; attempt <= COMMAND_POLL_MAX_ATTEMPTS; attempt++) {
          if (!mountedRef.current) return;
          await delay(EXECUTION_POLL_DELAY_MS);
          if (!mountedRef.current) return;
          setWaitingMessage(
            `Waiting for execution to start... (${attempt}/${COMMAND_POLL_MAX_ATTEMPTS})`,
          );

          try {
            const jobWithExecution = await tdApi.getJob(job.id, { embed: "execution" });
            if (!mountedRef.current) return;

            if (jobWithExecution?.execution?.id) {
              const executionId = jobWithExecution.execution.id;
              console.log(
                `[Execution] Found execution #${executionId} for job #${job.id} on attempt ${attempt}, fetching full data...`,
              );

              // Fetch the full execution directly to ensure all computed fields are present
              const fullExecution = await tdApi.getExecution(executionId);
              if (!mountedRef.current) return;

              if (!fullExecution) {
                console.error(`[Execution] Failed to fetch full execution #${executionId}`);
                continue; // retry on next attempt
              }
              upsertExecution(fullExecution);

              // Toast is shown by JobPollingManager - just navigate
              setRunning(false);
              setWaitingMessage("");
              navigate("/executions");
              return;
            }

            // Check if job failed
            if (jobWithExecution?.status_name === "failed") {
              showToast({
                title: "Execution failed",
                message: jobWithExecution.error_message || "Unknown error",
                type: "danger",
                timeout: 15000,
              });
              setRunning(false);
              setWaitingMessage("");
              return;
            }
          } catch (error) {
            console.error(`[Execution] Error polling for execution (attempt ${attempt}):`, error);
          }
        }

        // Max attempts reached - execution didn't start in time
        if (!mountedRef.current) return;
        showToast({
          title: "Execution timeout",
          message: `Job #${job.id} was created but execution didn't start within expected time. Check the Jobs page for status.`,
          type: "warning",
          timeout: 15000,
          actions: [
            {
              label: "View Jobs",
              onClick: () => navigate("/jobs"),
            },
          ],
        });
        setRunning(false);
        setWaitingMessage("");
      }
    } catch (error) {
      console.error(`${type} submission error:`, error);
      showToast({
        title: "Error",
        message: `Failed to start ${type}. Please try again.`,
        type: "danger",
      });
      setRunning(false);
    }
  };

  // Dynamic labels based on type
  const entityLabel = type === "backtest" ? "Backtest" : "Execution";
  const actionLabel = type === "backtest" ? "Run Backtest" : "Start Execution";

  return (
    <div>
      <CRow className="h-100 g-2">
        {/* Left Side - Common Settings */}
        <CCol lg={6}>
          <CCard className="RunSetupCard h-100">
            <CCardHeader className="py-1 px-2">
              <h6 className="mb-0">{entityLabel} Setup</h6>
            </CCardHeader>

            <CCardBody className="p-2">
              <CForm onSubmit={handleSubmit}>
                <CommonSettingsBlock
                  strategyId={formData.strategyId}
                  exchangeName={formData.exchangeName}
                  instrument={formData.instrument}
                  timeframe={formData.timeframe}
                  startDateTime={formData.startDateTime}
                  endDateTime={formData.endDateTime}
                  investmentAmount={formData.investmentAmount}
                  cumulativeInvestment={formData.cumulativeInvestment}
                  onChange={handleInputChange}
                  disabled={running}
                  hideDates={type === "execution"}
                />
              </CForm>
            </CCardBody>
          </CCard>
        </CCol>

        {/* Right Side - Config and Execution Settings */}
        <CCol lg={6}>
          <div className="RightColumnCards d-flex flex-column h-100 gap-2">
            <CCard className="TemplateConfigCard">
              <CCardHeader className="py-1 px-2">
                <h6 className="mb-0">Config</h6>
              </CCardHeader>
              <CCardBody className="p-2">
                <CJsonTextArea
                  value={formData.config}
                  onChange={(event) => handleInputChange("config", event.target.value)}
                  disabled={running}
                  rows={type === "execution" ? 10 : 22}
                  placeholder="Override configuration as JSON..."
                />
              </CCardBody>
            </CCard>

            {type === "execution" && (
              <CCard className="LiveExecutionSettingsCard">
                <CCardHeader className="py-1 px-2">
                  <h6 className="mb-0">Live Execution Settings</h6>
                </CCardHeader>
                <CCardBody className="p-2">
                  <ExecutionConfigAuth
                    exchangeName={formData.exchangeName}
                    exchangeEnvironment={formData.exchangeEnvironment}
                    exchangeType={exchangeType}
                    quoteCurrency={quoteCurrency || undefined}
                    onEnvironmentChange={(env) => handleInputChange("exchangeEnvironment", env)}
                    onKeyPathChange={(keyPath) => handleInputChange("infisicalKeyPath", keyPath)}
                    onSecretPathChange={(secretPath) =>
                      handleInputChange("infisicalSecretPath", secretPath)
                    }
                    onPasswordPathChange={(passwordPath) =>
                      handleInputChange("infisicalPasswordPath", passwordPath)
                    }
                    disabled={running}
                  />
                </CCardBody>
              </CCard>
            )}

            <div className="RunButtonCard d-grid">
              <CButton
                type="button"
                color="primary"
                size="lg"
                disabled={running}
                onClick={handleSubmit}
              >
                {running ? (
                  <CSpinnerText
                    text={
                      waitingMessage ||
                      (type === "backtest" ? "Running Backtest..." : "Starting Execution...")
                    }
                    gap="2"
                  />
                ) : (
                  actionLabel
                )}
              </CButton>
            </div>
          </div>
        </CCol>
      </CRow>
    </div>
  );
};
